import { NavLink, useLocation } from "react-router-dom";
import { type FC, useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { usePermissions } from "../../context/PermissionContext";
import { useSettings } from "../../context/SettingsContext";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  FileText,
  RotateCcw,
  Users,
  ShoppingBag,
  RefreshCw,
  CreditCard,
  BarChart3,
  TrendingUp,
  Settings,
  Ruler,
  Archive,
  ChevronDown,
  Briefcase,
  Truck,
  ClipboardCheck,
  ArrowLeftRight,
  Clock,
  FileSpreadsheet,
  type LucideIcon,
} from "lucide-react";


interface SubNavItem {
  label: string;
  to: string;
  module?: string;
  roles?: string[];
  end?: boolean;
}

interface NavItem {
  label: string;
  to?: string;
  icon: LucideIcon;
  module?: string;
  roles?: string[];
  end?: boolean;
  subItems?: SubNavItem[];
}

const NAV_ITEMS: NavItem[] = [
  {
    label: "Dashboard",
    to: "/dashboard",
    icon: LayoutDashboard,
  },
  {
    label: "Inventory Management",
    to: "/inventory/products",
    icon: Package,
    module: "inventory",
    roles: ["admin", "manager", "staff"],
  },
  {
    label: "Stocktake & Adjustments",
    to: "/inventory/stocktake",
    icon: ClipboardCheck,
    module: "inventory",
    roles: ["admin", "manager"],
  },
  {
    label: "Warehouse Transfers",
    to: "/inventory/transfers",
    icon: ArrowLeftRight,
    module: "inventory",
    roles: ["admin", "manager", "staff"],
  },
  {
    label: "Purchases",
    to: "/purchases",
    icon: ShoppingCart,
    module: "purchases",
    roles: ["admin", "manager", "staff"],
    end: true,
  },
  {
    label: "Purchase Returns",
    to: "/purchases/returns",
    icon: RotateCcw,
    module: "purchase-returns",
    roles: ["admin", "manager"],
  },
  {
    label: "Goods Receipts (GRN)",
    to: "/purchases/goods-receipts",
    icon: Truck,
    module: "purchases",
    roles: ["admin", "manager", "staff"],
  },
  {
    label: "Supplier Bills",
    to: "/purchases/supplier-bills",
    icon: FileText,
    module: "purchases",
    roles: ["admin", "manager"],
  },
  {
    label: "Suppliers",
    to: "/creditors/suppliers",
    icon: Users,
    module: "purchases",
    roles: ["admin", "manager"],
  },
  {
    label: "Supplier AP Aging",
    to: "/creditors/aging-report",
    icon: Clock,
    module: "purchases",
    roles: ["admin", "manager"],
  },
  {
    label: "Sales Quotations",
    to: "/sales/quotes",
    icon: FileSpreadsheet,
    module: "sales-orders",
    roles: ["admin", "manager", "cashier"],
  },
  {
    label: "Sales Orders",
    to: "/sales/orders",
    icon: ShoppingBag,
    module: "sales-orders",
    roles: ["admin", "manager", "cashier"],
  },
  {
    label: "Sales Invoices",
    to: "/sales/invoices",
    icon: FileText,
    module: "sales-invoices",
    roles: ["admin", "manager", "cashier"],
  },
  {
    label: "Sales Returns / Credit Notes",
    to: "/credit-notes",
    icon: RefreshCw,
    module: "credit-notes",
    roles: ["admin", "manager"],
  },
  {
    label: "Customers",
    to: "/debtors/customers",
    icon: Users,
    module: "sales-invoices",
    roles: ["admin", "manager"],
  },
  {
    label: "Payments",
    to: "/payments",
    icon: CreditCard,
    module: "payments",
    roles: ["admin", "manager", "cashier"],
  },
  {
    label: "Expenses",
    to: "/expenses",
    icon: BarChart3,
    module: "expenses",
    roles: ["admin", "manager", "cashier"],
  },
  {
    label: "Reports",
    to: "/reports",
    icon: TrendingUp,
    module: "reports",
    roles: ["admin", "manager"],
  },
  {
    label: "Asset Management",
    icon: Archive,
    module: "assets",
    roles: ["admin", "manager", "staff"],
    subItems: [
      { label: "Assets List", to: "/assets", module: "assets", roles: ["admin", "manager", "staff"] },
      { label: "Asset Categories", to: "/assets/categories", module: "assets", roles: ["admin", "manager"] },
      { label: "Asset Reports", to: "/assets/reports", module: "assets", roles: ["admin", "manager", "staff"] },
    ],
  },
  {
    label: "Human Resources",
    icon: Briefcase,
    module: "hr",
    roles: ["admin"],
    subItems: [
      { label: "Employees", to: "/expenses/employees", module: "hr", roles: ["admin"] },
      { label: "Payroll Summary", to: "/expenses/payroll", module: "hr", roles: ["admin"] },
      { label: "Manage Positions", to: "/expenses/positions", module: "hr", roles: ["admin"] },
    ],
  },
  {
    label: "Utilities",
    icon: Ruler,
    module: "settings",
    roles: ["admin", "manager"],
    subItems: [
      { label: "Categories", to: "/settings/categories", module: "categories" },
      { label: "Units of Measure", to: "/settings/units-of-measure", module: "settings", roles: ["admin", "manager"] },
      { label: "Warehouses", to: "/inventory/warehouses", module: "inventory", roles: ["admin", "manager"] },
      { label: "Warehouse Transfers", to: "/inventory/transfers", module: "inventory", roles: ["admin", "manager", "staff"] },
      { label: "Brands", to: "/inventory/brands", module: "inventory", roles: ["admin", "manager"] },
    ],
  },
  {
    label: "Settings",
    icon: Settings,
    module: "settings",
    subItems: [
      { label: "Shop Profile", to: "/settings/shop", module: "settings" },
      { label: "Invoice/Print Settings", to: "/settings/invoice", module: "settings" },
      { label: "Accounting Periods", to: "/settings/accounting-periods", module: "settings", roles: ["admin"] },
      { label: "Document Manager", to: "/settings/documents", module: "settings", roles: ["admin", "manager"] },
      { label: "GST India Settings", to: "/settings/gst-india", module: "settings", roles: ["admin"] },
      { label: "Permissions", to: "/settings/permissions", module: "settings" },
      { label: "User Management", to: "/settings/users", module: "user-management" },
      { label: "Audit Log", to: "/settings/audit-log", module: "audit-log" },
      { label: "Subscriptions & Plugins", to: "/settings/subscriptions", module: "settings", roles: ["admin"] },
    ],
  },
];

function isModuleEnabled(module: string, enabledModules: string[] | null | undefined): boolean {
  if (!enabledModules) return true;
  
  // Map frontend sub-modules to the core subscription package keys
  const mapping: Record<string, string> = {
    "inventory": "inventory",
    "categories": "inventory",
    "units-of-measure": "inventory",
    
    "sales": "sales",
    "sales-orders": "sales",
    "sales-invoices": "sales",
    "credit-notes": "sales",
    "payments": "sales",
    "customers": "sales",
    
    "purchases": "purchases",
    "purchase-returns": "purchases",
    "suppliers": "purchases",
    
    "accounting": "accounting",
    "reports": "accounting",
    
    "assets": "assets",
    "expenses": "expenses",
    "hr": "hr",
    
    "settings": "settings", 
    "user-management": "settings", 
    "audit-log": "settings", 
  };

  const corePackage = mapping[module] || module;
  // Core configuration files (settings/users/audit logs) are always accessible
  if (corePackage === "settings") return true;
  return enabledModules.includes(corePackage);
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const Sidebar: FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { role } = useAuth();
  const { hasAccess } = usePermissions();
  const { enabledModules, kraPin, currency } = useSettings();
  const location = useLocation();

  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({});

  const toggleMenu = (label: string) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  // Filter visible items based on modules/roles/plans
  let visibleItems = NAV_ITEMS.map((item) => {
    if (item.module !== "dashboard" && item.module) {
      // 1. Subscription Plan entitlement check
      if (!isModuleEnabled(item.module, enabledModules)) return null;
      // 2. Permission matrix entitlement check
      if (item.module !== "hr" && !hasAccess(item.module)) return null;
      // 3. User Role access check
      if (item.roles && (!role || !item.roles.includes(role))) return null;
    }

    if (item.subItems) {
      const filteredSubs = item.subItems.filter((sub) => {
        if (!sub.module) return true;
        if (!isModuleEnabled(sub.module, enabledModules)) return false;
        if (sub.module !== "hr" && !hasAccess(sub.module)) return false;
        if (sub.roles && (!role || !sub.roles.includes(role))) return false;
        return true;
      });
      if (filteredSubs.length === 0) return null;
      return { ...item, subItems: filteredSubs };
    }

    return item;
  }).filter((item): item is NavItem => item !== null);

  // For super_admin, override with platform administration features
  if (role === "super_admin") {
    visibleItems = [
      {
        label: "SaaS Platform",
        to: "/platform",
        icon: LayoutDashboard,
      }
    ];
  }

  // Auto-expand active parent menus on location change
  useEffect(() => {
    visibleItems.forEach((item) => {
      if (item.subItems) {
        const isChildActive = item.subItems.some((sub) => location.pathname === sub.to);
        if (isChildActive) {
          setExpandedMenus((prev) => ({ ...prev, [item.label]: true }));
        }
      }
    });
  }, [location.pathname]);

  return (
    <>
      {/* Mobile overlay backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-20 bg-black/40 lg:hidden"
          aria-hidden="true"
          onClick={onClose}
        />
      )}

      {/* Sidebar panel */}
      <aside
        className={[
          "fixed inset-y-0 left-0 z-30 flex w-64 flex-col bg-gray-900 transition-transform duration-300 ease-in-out",
          "lg:static lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full",
        ]
          .filter(Boolean)
          .join(" ")}
        aria-label="Main navigation"
      >
        {/* Logo / brand area */}
        <div className="flex h-16 shrink-0 items-center justify-between px-4">
          <span className="text-lg font-bold text-white">SSI-ERP</span>
          <button
            type="button"
            className="rounded-md p-1 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white lg:hidden"
            onClick={onClose}
            aria-label="Close navigation"
          >
            <svg
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Nav links */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <ul className="space-y-1" role="list">
            {visibleItems.map((item) => {
              const Icon = item.icon;
              const hasSubs = !!item.subItems;
              const isExpanded = !!expandedMenus[item.label];

              if (hasSubs && item.subItems) {
                return (
                  <li key={item.label} className="space-y-1">
                    <button
                      type="button"
                      onClick={() => toggleMenu(item.label)}
                      className={[
                        "flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-gray-300 hover:bg-gray-800 hover:text-white transition-colors focus:outline-none",
                        isExpanded ? "text-white bg-gray-800/40" : "",
                      ].join(" ")}
                    >
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                        {item.label}
                      </div>
                      <ChevronDown
                        className={[
                          "h-4 w-4 shrink-0 transition-transform duration-200 text-gray-400",
                          isExpanded ? "rotate-180 text-white" : "",
                        ].join(" ")}
                      />
                    </button>
                    {isExpanded && (
                      <ul className="pl-9 space-y-1">
                        {item.subItems.map((sub) => (
                          <li key={sub.to}>
                            <NavLink
                              to={sub.to}
                              end={sub.end}
                              onClick={onClose}
                              className={({ isActive }) =>
                                [
                                  "block rounded-md px-3 py-2 text-xs font-medium transition-colors",
                                  isActive
                                    ? "bg-primary-600 text-white"
                                    : "text-gray-400 hover:bg-gray-800 hover:text-white",
                                ].join(" ")
                              }
                            >
                              {sub.label}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              }

              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to || ""}
                    end={item.end}
                    onClick={onClose}
                    className={({ isActive }) =>
                      [
                        "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                        isActive
                          ? "bg-primary-600 text-white"
                          : "text-gray-300 hover:bg-gray-800 hover:text-white",
                      ].join(" ")
                    }
                  >
                    <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                    {item.label}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Role badge at bottom */}
        {role && (
          <div className="shrink-0 border-t border-gray-700 px-4 py-3">
            {kraPin && (
              <p className="text-xs text-gray-500 mb-1">
                <span className="font-medium">{currency === "INR" ? "PAN:" : "KRA PIN:"}</span> {kraPin}
              </p>
            )}
            <span className="inline-flex items-center rounded-full bg-gray-700 px-2.5 py-0.5 text-xs font-medium capitalize text-gray-300">
              {role}
            </span>
          </div>
        )}
      </aside>
    </>
  );
};
